<template>
  <DialogLayout v-model="isOpen" :title="modalTitle" width="900px">
    <AlertsAnimated :alerts="localAlerts" />

    <div class="pa-4 border-bottom">
      <v-row no-gutters class="mb-3">
        <v-col cols="7">
          <div class="task-details">
            <div class="mb-2">
              <span class="font-weight-medium">Task ID: </span>
              <span>{{ task.taskId }}</span>
            </div>
            <div class="mb-2">
              <span class="font-weight-medium">Due Date: </span>
              <span>{{ formatDateSlashUTC(task.dueAt) }}</span>
            </div>
            <div v-if="task?.description">
              <span class="font-weight-medium">Description: </span>
              <span>{{ task.description }}</span>
            </div>
          </div>
        </v-col>

        <v-col cols="5">
          <div class="qr-section">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="font-weight-medium">Mobile Access:</span>
              <v-btn
                x-small
                color="primary"
                outlined
                @click="showQRDialog = true"
              >
                <v-icon x-small left>mdi-qrcode</v-icon>
                Generate QR
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div v-if="loadingUnits" class="pa-4">
      <v-skeleton-loader
        v-for="n in 5"
        :key="n"
        :loading="loadingUnits"
        type="card"
        height="70"
        class="mb-2"
      />
    </div>

    <div v-else-if="!loadingUnits && overlockUnits.length === 0" class="pa-4">
      <v-card outlined class="text-center pa-4">
        <v-icon size="64" color="grey lighten-2" class="mb-4">
          mdi-information-outline
        </v-icon>
        <p class="grey--text text-h6 mb-0">There are no disabled lien units</p>
      </v-card>
    </div>

    <div v-else class="">
      <div class="checklist-header pa-4 grey lighten-3">
        <div class="d-flex justify-space-between align-center">
          <span class="font-weight-bold">Units with Disabled Lien</span>
          <span class="text-caption grey--text">
            {{ completedCount }}/{{ totalCount }} overlock
          </span>
        </div>
        <v-progress-linear
          :value="progressPercentage"
          color="success"
          height="4"
          class="mt-2"
        />
      </div>

      <div class="checklist-items">
        <div
          v-for="(unit, index) in overlockUnits"
          :key="unit.id"
          class="checklist-item pa-4"
          :class="{
            completed: unit.completed,
            'border-bottom': index < overlockUnits.length - 1,
            'locked-item': unit.lockedFromResponse,
          }"
        >
          <v-row no-gutters align="center">
            <v-col cols="1">
              <v-checkbox
                v-model="unit.completed"
                :disabled="unit.lockedFromResponse"
                class="ma-0 pa-0"
                color="success"
                hide-details
                @change="updateUnitStatus(unit, index)"
              />
            </v-col>

            <v-col cols="7">
              <div
                class="unit-description"
                :class="{
                  'completed-text': unit.completed,
                  'locked-text': unit.lockedFromResponse,
                }"
              >
                {{ unit.name }}
                <v-chip
                  v-if="unit.lockedFromResponse && isApplyOverlock"
                  x-small
                  color="error"
                  text-color="white"
                  class="ml-2"
                >
                  <v-icon x-small left>mdi-lock</v-icon>
                  Overlocked
                </v-chip>
                <v-chip
                  v-if="
                    unit.completed &&
                    !isApplyOverlock &&
                    task?.resolution === 'done'
                  "
                  x-small
                  color="success"
                  text-color="white"
                  class="ml-2"
                >
                  <v-icon x-small left>mdi-check-circle</v-icon>
                  Removed Overlock
                </v-chip>
              </div>
              <div
                v-if="getUnitAccountInfo(unit)"
                class="text-caption grey--text mt-1"
              >
                {{ getUnitAccountInfo(unit) }}
              </div>
            </v-col>

            <v-col cols="4">
              <div class="text-right">
                <v-text-field
                  v-model="unit.comment"
                  placeholder="Add comment..."
                  :disabled="unit.lockedFromResponse"
                  dense
                  outlined
                  hide-details
                  class="comment-field mt-1"
                  @change="updateUnitComment(unit, index)"
                />
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <template #actions>
      <v-btn
        v-if="shouldShowActionButton"
        color="success"
        :disabled="!canCompleteTask"
        @click="completeOverlockTask"
        depressed
        large
        :loading="completing"
        class="ml-2"
      >
        {{ actionButtonText }}
      </v-btn>
    </template>

    <QRCodeDialog
      v-model="showQRDialog"
      title="Mobile Task Access"
      description="Scan this QR code with your mobile phone or tablet to manage this task."
      :url-generator="generateOverlockUrl"
    />
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import QRCodeDialog from "../mobile/QRCodeDialog.vue";
import { UPDATE_TASK, GET_DISABLED_LIEN_UNITS } from "./graphql";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";

export default {
  name: "DialogOverlockModal",
  components: {
    DialogLayout,
    AlertsAnimated,
    QRCodeDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      required: false,
      default: null,
    },
  },
  setup() {
    const { defaultOrganization } = useUser();
    const { addLocalAlert, clearAllAlerts, localAlerts } = useAlerts();

    return {
      defaultOrganization,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
    };
  },
  data() {
    return {
      overlockUnits: [],
      loadingUnits: false,
      completing: false,
      showQRDialog: false,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
        if (!value) {
          this.$emit("close");
          this.clearAllAlerts();
        }
      },
    },
    modalTitle() {
      return this.task?.title || "Manage Overlocks";
    },
    isApplyOverlock() {
      return (
        this.task?.title?.toLowerCase().includes("apply") ||
        this.task?.description?.toLowerCase().includes("apply")
      );
    },
    hasAnyCompletedUnits() {
      return this.overlockUnits.some((unit) => unit.completed);
    },
    completedCount() {
      return this.overlockUnits.filter((unit) => unit.completed).length;
    },
    totalCount() {
      return this.overlockUnits.length;
    },
    progressPercentage() {
      return this.totalCount > 0
        ? (this.completedCount / this.totalCount) * 100
        : 0;
    },
    shouldShowActionButton() {
      return this.task?.resolution !== "done" && !this.loadingUnits;
    },
    canCompleteTask() {
      return (
        this.overlockUnits.length === 0 ||
        this.hasAnyCompletedUnits ||
        this.overlockUnits.every((unit) => unit.completed)
      );
    },
    actionButtonText() {
      if (this.overlockUnits.length === 0) {
        return "Mark as Done";
      }
      if (!this.isApplyOverlock && this.hasAnyCompletedUnits) {
        return "Remove Overlock";
      }
      if (this.overlockUnits.every((unit) => unit.completed)) {
        return "Complete Task";
      }
      return this.isApplyOverlock ? "Apply Overlocks" : "Remove Overlock";
    },
    formattedExpiryTime() {
      if (!this.qrExpirationTime) return null;
      return formatTime12hr(this.qrExpirationTime);
    },
    timeRemainingDuration() {
      if (!this.qrExpirationTime) return null;
      return durationInWords(new Date().toISOString(), this.qrExpirationTime);
    },
    computedQrError() {
      if (!this.qrError) return null;

      return {
        ...this.qrError,
        expiryTime: this.formattedExpiryTime,
        timeRemaining: this.timeRemainingDuration,
      };
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal && this.task) {
          this.clearAllAlerts();
          this.initializeModal();
        }
      },
      immediate: true,
    },
    task: {
      handler(newTask) {
        if (newTask && this.value) {
          this.initializeModal();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async initializeModal() {
      await this.loadDisabledLienUnits();
      this.populateExistingChecklist();
    },

    async loadDisabledLienUnits() {
      this.loadingUnits = true;
      try {
        if (this.task?.resolution === "done") {
          this.overlockUnits = this.getUnitsFromChecklist();
          return;
        }

        if (!this.isApplyOverlock) {
          this.overlockUnits = [
            {
              id: this.task.organization.id,
              name: this.task.organization.name,
              completed: false,
              comment: "",
              required: true,
              lockedFromResponse: false,
            },
          ];
          return;
        }

        const result = await this.$apollo.query({
          query: GET_DISABLED_LIEN_UNITS,
          variables: {
            facilityId: this.task.organization.id,
          },
          fetchPolicy: "network-only",
        });

        const units = result.data?.organizationDescendants || [];

        this.overlockUnits = units.map((unit) => ({
          id: unit.id,
          name: unit.name,
          type: unit.type,
          subtype: unit.subtype,
          billingAccounts: unit.billingAccounts || [],
          completed: false,
          comment: "",
          required: true,
          lockedFromResponse: false,
        }));
      } catch (error) {
        console.error("Error loading disabled lien units:", error);
        this.addLocalAlert({
          type: "error",
          message: "Error loading units. Please try again.",
          timeout: 5,
        });
        this.overlockUnits = [];
      } finally {
        this.loadingUnits = false;
      }
    },

    getUnitsFromChecklist() {
      return this.task.actions.checklist.map((checklistItem) => ({
        id: checklistItem.id,
        name: checklistItem.description,
        completed: Boolean(checklistItem.completed),
        comment: checklistItem.comment || "",
        required: true,
        lockedFromResponse: Boolean(checklistItem.completed),
      }));
    },

    populateExistingChecklist() {
      if (
        this.task?.actions?.checklist &&
        Array.isArray(this.task.actions.checklist)
      ) {
        this.task.actions.checklist.forEach((checklistItem) => {
          const unit = this.overlockUnits.find(
            (u) => u.name === checklistItem.description
          );
          if (unit) {
            unit.completed = Boolean(checklistItem.completed);
            unit.comment = checklistItem.comment || "";
            if (checklistItem.completed) {
              unit.lockedFromResponse = true;
            }
          }
        });
      }
    },

    getUnitAccountInfo(unit) {
      if (!unit.billingAccounts || unit.billingAccounts.length === 0) {
        return null;
      }

      const account = unit.billingAccounts[0];
      let info = `Email: ${account.name || "N/A"}`;

      if (account.users && account.users.length > 0) {
        const user = account.users[0];
        if (user.firstName || user.lastName) {
          info += `| User : ${user.firstName || ""} ${
            user.lastName || ""
          }`.trim();
        }
      }

      return info;
    },

    updateUnitStatus(unit, index) {
      if (unit.lockedFromResponse && !unit.completed) {
        unit.completed = true;
        return;
      }

      this.$emit("task-updated", {
        taskId: this.task.id,
        unitIndex: index,
        completed: unit.completed,
        unitName: unit.name,
        type: "local_update",
      });
    },

    updateUnitComment(unit, index) {
      if (unit.lockedFromResponse) {
        return;
      }

      this.$emit("task-updated", {
        taskId: this.task.id,
        unitIndex: index,
        comment: unit.comment,
        unitName: unit.name,
        type: "local_update",
      });
    },

    generateOverlockUrl(qrToken) {
      const baseUrl = window.location.origin;
      const facilityId = this.defaultOrganization.id;
      const taskId = this.task.id;

      const actionType = this.isApplyOverlock
        ? "manage-overlock"
        : "remove-overlock";

      return `${baseUrl}/manage-tasks/${taskId}/${actionType}?qrToken=${encodeURIComponent(
        qrToken
      )}&facility=${facilityId}`;
    },

    async completeOverlockTask() {
      if (!this.task) return;

      this.clearAllAlerts();
      this.completing = true;

      try {
        let resolution = "done";
        let checklistActions = [];

        if (this.overlockUnits.length === 0) {
          resolution = "done";
          checklistActions = [];
        } else if (this.overlockUnits.every((unit) => unit.completed)) {
          resolution = "done";
          checklistActions = this.overlockUnits.map((unit) => ({
            id: unit.id,
            completed: true,
            comment: unit.comment || "",
            description: unit.name,
          }));
        } else {
          checklistActions = this.overlockUnits.map((unit) => ({
            id: unit.id,
            completed: unit.completed,
            comment: unit.comment || "",
            description: unit.name,
          }));
          resolution = this.task.resolution;
        }

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: resolution,
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: checklistActions,
            },
          },
        });

        this.$emit("task-updated", {
          type: "complete_overlock_task",
          taskId: this.task.id,
          checklist: this.overlockUnits,
          resolution: resolution,
        });

        if (this.overlockUnits.length === 0) {
          this.addLocalAlert({
            type: "success",
            message:
              "Task marked as completed - no units with disabled lien found.",
          });
        } else if (this.overlockUnits.every((unit) => unit.completed)) {
          this.addLocalAlert({
            type: "success",
            message: this.isApplyOverlock
              ? "All overlocks completed successfully!"
              : "Overlock removed successfully!",
          });
        } else {
          const actionText = this.isApplyOverlock ? "applied" : "removed";
          const completedCount = this.completedCount;
          this.addLocalAlert({
            type: "success",
            message: `Overlocks ${actionText} for ${completedCount} units successfully!`,
          });
        }
      } catch (error) {
        console.error("Error completing overlock task:", error);

        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          error.graphQLErrors.forEach((graphQLError) => {
            this.addLocalAlert({
              type: "error",
              message: graphQLError.message,
            });
          });
        } else {
          this.addLocalAlert({
            type: "error",
            message:
              error.message ||
              "Failed to complete overlock task. Please try again.",
          });
        }
      } finally {
        this.completing = false;

        setTimeout(() => {
          this.handleClose();
        }, 1000);
      }
    },

    async handleClose() {
      if (this.task && this.task.resolution !== "done") {
        try {
          let existingChecklist = [];
          if (
            this.task.actions?.checklist &&
            Array.isArray(this.task.actions.checklist)
          ) {
            existingChecklist = this.task.actions.checklist.map((item) => ({
              id: item.id,
              completed: item.completed,
              comment: item.comment || "",
              description: item.description || "",
            }));
          }

          if (existingChecklist.length === 0) {
            await this.$apollo.mutate({
              mutation: UPDATE_TASK,
              variables: {
                id: this.task.id,
                assigneeId: this.task.assignee.id,
                description: this.task.description,
                dueAt: this.task.dueAt,
                organizationId: this.task.organization.id,
                priority: this.task.priority,
                resolution: "done",
                taskTypeId: this.task.taskType.id,
                title: this.task.title,
                actions: {
                  checklist: existingChecklist,
                },
              },
            });

            this.$emit("task-updated", {
              type: "close_overlock_task",
              taskId: this.task.id,
              resolution: "done",
            });
          }
        } catch (error) {
          console.error("Error updating task on close:", error);
        }
      }

      this.isOpen = false;
      this.overlockUnits = [];
    },
    formatDateSlashUTC,
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.checklist-header {
  border-bottom: 1px solid #e0e0e0;
}

.checklist-items {
  max-height: 50vh;
  overflow-y: auto;
}

.checklist-item {
  transition: background-color 0.2s ease;
}

.checklist-item:hover {
  background-color: #fafafa;
}

.checklist-item.completed {
  background-color: #f1f8e9;
}

.checklist-item.locked-item {
  background-color: #f8f9fa;
  border-left: 4px solid #f44336;
}

.checklist-item.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.unit-description {
  font-size: 15px;
  line-height: 1.5;
  color: #333;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.completed-text {
  color: #666 !important;
}

.locked-text {
  color: #f44336 !important;
  font-weight: 600;
}

.required-text {
  color: #ff9800;
  font-weight: bold;
  font-size: 12px;
}

.comment-field {
  max-width: 200px;
}

.comment-field.v-input--is-disabled {
  opacity: 0.6;
}

.task-details {
  padding-right: 16px;
}

.qr-section {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
}

.qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.qr-canvas {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-container {
  padding: 16px 0;
}

.error-content p {
  margin-bottom: 8px;
}

.error-content .caption {
  color: rgba(0, 0, 0, 0.6);
}

@media (max-width: 768px) {
  .task-details {
    padding-right: 8px;
  }

  .qr-section {
    padding: 8px;
  }
}
</style>
